/**
 * @description
 * Custom hook for managing settings data
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { api, type Setting } from '../../../services/api';
import { UseSettingsDataReturn, type Client } from '../types';

/**
 * Custom hook for fetching and managing settings data
 */
export function useSettingsData(): UseSettingsDataReturn {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const settingsData = await api.getSettings();
      setSettings(settingsData);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const clientList = await api.getClients();
      setClients(clientList);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to fetch clients');
    }
  };

  const refetch = useCallback(async () => {
    await Promise.all([fetchSettings(), fetchClients()]);
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return {
    settings,
    clients,
    loading,
    refetch,
  };
}
